import type { TicketMessage } from '@/types/ticket'
import { SystemMessageRow } from './SystemMessageRow'
import { AttachmentList } from '../attachments/AttachmentList'
import { AvatarInitials } from '@/components/ui/avatar-initials'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { EtainablBadge } from '@/components/etainabl-mark'
import { formatDayLabel, formatTime } from '@/lib/dates'

function authorName(message: TicketMessage): string {
  return message.author?.name ?? 'Unknown'
}

function isStaff(message: TicketMessage): boolean {
  return Boolean(message.author?.isAdmin) || message.authorRole === 'admin'
}

// Uppercase micro label between hairlines, grouping the thread by calendar
// day so individual messages only need a time.
function DayDivider({ label }: { label: string }) {
  return (
    <div
      className="my-4 flex items-center gap-3 text-etn-micro font-semibold uppercase tracking-[0.05em] text-etn-text-soft before:h-px before:flex-1 before:bg-etn-border-row after:h-px after:flex-1 after:bg-etn-border-row"
      data-testid="day-divider"
    >
      {label}
    </div>
  )
}

function MessageRow({ ticketId, message }: { ticketId: string; message: TicketMessage }) {
  const staff = isStaff(message)
  // Optimistic messages (still in flight) render dimmed until the server
  // confirms and the refetch swaps in the real document.
  const pending = message._id.startsWith('optimistic-')
  // Who said what has to be readable without reading: customer messages sit
  // left on a white card, staff replies sit right on a green tint. Internal
  // notes keep their own amber card — they are staff-side too, so they stay on
  // the right, but must never be mistakable for something the customer saw.
  const cardClass = message.isInternal
    ? 'bg-etn-warning-subtle border-etn-warning-text/25'
    : staff
    ? 'bg-etn-accent-subtle border-etn-accent-subtle-border'
    : 'bg-etn-surface border-etn-border-hairline'

  return (
    <div
      className={`mb-3.5 flex max-w-[82%] gap-2.5 ${staff ? 'ml-auto flex-row-reverse' : ''} ${pending ? 'opacity-60' : ''}`}
      data-testid="message-row"
      data-author={staff ? 'staff' : 'customer'}
    >
      <AvatarInitials name={authorName(message)} size="md" className="mt-0.5 shrink-0" />
      <div className="min-w-0">
        <div className={`rounded-etn-card border px-3.5 py-2.5 shadow-etn-whisper ${cardClass}`}>
          <div className="mb-1 flex flex-wrap items-baseline gap-2 text-etn-micro text-etn-text-soft">
            <span className="text-etn-control font-semibold text-etn-text-cell">{authorName(message)}</span>
            {staff && <EtainablBadge />}
            {message.isInternal && <span className="font-semibold text-etn-warning-text">Internal note</span>}
            <span>{formatTime(message.createdAt)}</span>
          </div>
          <div className="prose prose-sm dark:prose-invert max-w-none text-etn-text-cell">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{message.body}</ReactMarkdown>
          </div>
          <AttachmentList ticketId={ticketId} attachments={message.attachments} />
        </div>
      </div>
    </div>
  )
}

// The one conversation layout: side-by-side cards under day dividers. Both the
// BAU ticket route and the tracker detail's Comments section mount this. No
// horizontal gutter of its own — the ticket page's panel and the tracker's
// section set different ones, so the host owns it.
export function MessageThread({ ticketId, messages }: { ticketId: string; messages: TicketMessage[] }) {
  let lastDay: string | null = null

  return (
    <div className="py-4">
      {messages.map(m => {
        const day = formatDayLabel(m.createdAt)
        const newDay = day !== lastDay
        lastDay = day
        return (
          <div key={m._id}>
            {newDay && <DayDivider label={day} />}
            {m.kind === 'system'
              ? <SystemMessageRow message={m} />
              : <MessageRow ticketId={ticketId} message={m} />}
          </div>
        )
      })}
    </div>
  )
}
